/**
 * Shared "click-to-edit text field" state machine used by player and group
 * name inputs.
 *
 * Each call site toggles a local edit mode, mirrors the current value into a
 * draft, focuses + selects the input on entry, and routes Enter / Escape /
 * blur to commit or cancel. Before this composable existed each component
 * duplicated its own `editingField` ref + `draft` ref + `inputRef` + watcher
 * + Enter/Escape/blur handlers.
 *
 * The host owns:
 *   - the bound `<input ref="inputRef" v-model="draft">` template;
 *   - calling `commitEdit()` from `@blur` and `@keydown.enter` on the input;
 *   - calling `startEdit()` from a button's `@click`;
 *   - calling `cancelEdit()` from `@keydown.escape` on the input (or letting
 *     the watcher auto-cancel via `editingField = null`).
 *
 * The composable owns the draft ref, the focus watcher, and the validation
 * gate. `commitEdit` is async because the provided `onCommit` may be async
 * (e.g. it hits a GraphQL mutation); we await so callers can chain.
 *
 * NOTE: `RoomSection.vue` uses a `contenteditable` span instead of `<input>`,
 * so it does not use this composable. The shared subset of the room rename
 * pattern (commit on blur, cancel on Escape reverting `innerText`) belongs in
 * `RoomSection` itself.
 */
import {
  computed,
  nextTick,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

export interface UseInlineEditableTextFieldOptions<TField extends string> {
  /**
   * The currently-committed value displayed outside edit mode. The composable
   * mirrors it into `draft` whenever a new edit session begins — callers
   * don't have to seed the draft themselves.
   */
  readonly currentValue: Ref<string>
  /** The field identifier whose edit session is open. */
  readonly field: TField
  /**
   * Permission gate. When `false`, `startEdit` is a no-op and `commitEdit`
   * discards the draft silently (mirrors the existing per-component behavior
   * for read-only officers / pool-only players).
   */
  readonly canEdit: ComputedRef<boolean>
  /**
   * Persistence hook. Only invoked on commit when the trimmed draft differs
   * from `currentValue`. May return a promise (e.g. a GraphQL mutation).
   */
  readonly onCommit: (newValue: string) => void | Promise<void>
  /**
   * Optional validator. Returns `true` when the draft is acceptable.
   * Defaults to "any string" (no-op). A draft that fails validation is
   * discarded without ever calling `onCommit`, matching the existing
   * per-component behavior of ignoring empty / placeholder text.
   */
  readonly validate?: (draft: string) => boolean
}

export interface UseInlineEditableTextFieldReturn<TField extends string> {
  /** The field being edited, or `null` when no edit session is open. */
  readonly editingField: Ref<TField | null>
  /** The draft string bound to `<input v-model="draft">`. */
  readonly draft: Ref<string>
  /**
   * Bound to the `<input ref="inputRef">` template. The composable focuses +
   * selects the input whenever `editingField` flips to the matching field.
   */
  readonly inputRef: Ref<HTMLInputElement | null>
  /** `true` whenever `editingField === field`. */
  readonly isEditing: ComputedRef<boolean>
  /** Open a new edit session (no-op when `canEdit` is false). */
  readonly startEdit: () => void
  /** Drop the draft without committing; `editingField` returns to `null`. */
  readonly cancelEdit: () => void
  /**
   * Commit the draft to `onCommit` (after trimming + validation). Resolves
   * the moment the field is closed — does NOT wait for `onCommit` to settle.
   */
  readonly commitEdit: () => Promise<void>
}

/**
 * Manage the click-to-edit state machine for a single field per call. A host
 * with multiple editable values invokes this composable once per value and
 * gives each invocation its own `inputRef`.
 */
export function useInlineEditableTextField<TField extends string>(
  opts: UseInlineEditableTextFieldOptions<TField>,
): UseInlineEditableTextFieldReturn<TField> {
  const editingField = ref<TField | null>(null) as Ref<TField | null>
  const draft = ref<string>('')
  const inputRef = ref<HTMLInputElement | null>(null)

  function isEditing(field: TField): boolean {
    return editingField.value === field
  }

  function startEdit(): void {
    if (!opts.canEdit.value) return
    draft.value = opts.currentValue.value
    editingField.value = opts.field
  }

  function cancelEdit(): void {
    editingField.value = null
  }

  /**
   * Commit the current draft. The caller wires this to `@blur` and
   * `@keydown.enter` on the bound input. Returns immediately after the field
   * closes — the underlying `onCommit` promise is intentionally detached so
   * a slow network mutation can't leave the input stuck "open".
   */
  async function commitEdit(): Promise<void> {
    const field = editingField.value
    if (!field) return
    // Mirror the existing read-only path: when permissions flip off mid-edit
    // we still close the input and discard the draft instead of leaking a
    // stale draft into the persisted store.
    if (!opts.canEdit.value) {
      editingField.value = null
      return
    }
    const next = draft.value.trim()
    if (opts.validate && !opts.validate(next)) {
      editingField.value = null
      return
    }
    if (next !== opts.currentValue.value) {
      // Fire-and-await: we record the promise locally so a future
      // enhancement can `await` if needed, but we still resolve
      // `commitEdit` synchronously (well, in a microtask) so the input
      // doesn't appear frozen on slow mutations.
      await opts.onCommit(next)
    }
    editingField.value = null
  }

  // Focus + select-all on edit session start so the user can immediately
  // overwrite the value. Deferred one tick so the input has been swapped
  // into the DOM (the conditional `<input v-if="editingField === ...">`
  // doesn't mount until `editingField` flips).
  watch(editingField, async (field) => {
    if (field !== opts.field) return
    await nextTick()
    const el = inputRef.value
    if (el) {
      el.focus()
      el.select()
    }
  })

  return {
    editingField,
    draft,
    inputRef,
    isEditing: computed<boolean>(() => isEditing(opts.field)),
    startEdit,
    cancelEdit,
    commitEdit,
  }
}
